import { ActionReducerMap } from '@ngrx/store';
/**
 * Every reducer module's default export is the reducer function itself. In
 * addition, each module should export a type or interface that describes
 * the state of the reducer plus any selector functions. The `* as`
 * notation packages up all of the exports into a single object.
 */
import { CUSTOMER_EFFECTS, CustomerReducer } from 'src/app/features/customers/state';
import { EMPLOYEE_EFFECTS, EmployeeReducer } from 'src/app/features/employees/state';
import { FIELDSERVICE_EFFECTS, FieldServiceReducer } from 'src/app/features/field-service/state';
import { MaintenanceEffects, MaintenanceReducer } from 'src/app/features/field-service/state/maintenance';
import { INCOMING_TOPICS_EFFECTS, IncomingTopicsReducer } from 'src/app/features/incoming-topics/state';
import { INCOMING_TOPICS_V2_EFFECTS, IncomingTopicsV2Reducer } from 'src/app/features/incoming-topics-v2/state';
import { MachineReducer, MACHINES_EFFECTS } from 'src/app/features/machines/state';
import { SERVICEVAN_EFFECTS, ServiceVanReducer } from 'src/app/features/servicevans/state';
import { TSC_ADMIN_EFFECTS, TscAdminReducer } from 'src/app/features/tsc-admin/state';
import { AccountEffects, AccountReducers } from './account';
import { CommentEffects, CommentReducer } from './comments';
import { DEFAULT_VIEWFILTER_EFFECTS, DefaultViewFilterReducer } from './default-view-filter';
import { FeatureToggleEffects } from './feature-toggle';
import * as FeatureToggleReducer from './feature-toggle/feature-toggle.reducer';
import { LayoutEffects, LayoutReducer } from './layout';
import { SHARED_MACHINE_EFFECTS, SharedMachinesReducer } from './machines';
import { NotificationEffects, NotificationReducer } from './notification';
import { NOTIFICATIONS_EFFECTS, NotificationsReducer } from './notifications';
import { LOG_MESSAGE_EFFECTS, LogMessageReducer } from '../../features/log-messages/state';

export type StateError = Error | string | null;

/**
 * As mentioned, we treat each reducer like a table in a database. This means
 * our top level state interface is just a map of keys to inner state types.
 */
export interface State {
  rsAccount: AccountReducers.AccountState;
  rsLayout: LayoutReducer.State;
  rsNotification: NotificationReducer.State;
  rsNotifications: NotificationsReducer.State;
  rsComments: CommentReducer.CommentState;
  // TODO make modularization work again :) Only put into the
  // root store in order to make feature selectors working
  // again
  // fsMachines: MachineReducer.MachineState;
  fsCustomers: CustomerReducer.CustomerState;
  fsEmployees: EmployeeReducer.EmployeeState;
  fsFieldService: FieldServiceReducer.FieldServiceState;
  fsIncomingTopics: IncomingTopicsReducer.IncomingTopicsState;
  fsIncomingTopicsV2: IncomingTopicsV2Reducer.IncomingTopicsV2State;
  fsMachines: MachineReducer.MachineState;
  fsServiceVans: ServiceVanReducer.ServiceVanState;
  fsFeatureToggles: FeatureToggleReducer.State;
  fsLogMessages: LogMessageReducer.LogMessageState;
  fsTMachines: SharedMachinesReducer.SharedMachineState;
  fsTscAdmin: TscAdminReducer.TscAdminState;
  fsDefaultViewFilter: DefaultViewFilterReducer.DefaultViewFilterState;
  fsMaintenances: MaintenanceReducer.State;
}

/**
 * Re-export all of our shared Effects.
 *
 * Registered in the `core` ngModule
 */
export const effects = [AccountEffects, LayoutEffects, NotificationEffects, FeatureToggleEffects, CommentEffects, MaintenanceEffects]
  .concat(NOTIFICATIONS_EFFECTS)
  .concat(SHARED_MACHINE_EFFECTS)
  .concat(CUSTOMER_EFFECTS)
  .concat(EMPLOYEE_EFFECTS)
  .concat(FIELDSERVICE_EFFECTS)
  .concat(INCOMING_TOPICS_EFFECTS)
  .concat(INCOMING_TOPICS_V2_EFFECTS)
  .concat(MACHINES_EFFECTS)
  .concat(SERVICEVAN_EFFECTS)
  .concat(LOG_MESSAGE_EFFECTS)
  .concat(TSC_ADMIN_EFFECTS)
  .concat(DEFAULT_VIEWFILTER_EFFECTS);

export const reducers: ActionReducerMap<State, any> = {
  rsAccount: AccountReducers.reducer,
  rsLayout: LayoutReducer.reducer,
  rsNotification: NotificationReducer.reducer,
  rsNotifications: NotificationsReducer.reducer,
  rsComments: CommentReducer.reducer,

  fsCustomers: CustomerReducer.reducer,
  fsEmployees: EmployeeReducer.reducer,
  fsFieldService: FieldServiceReducer.reducer,
  fsIncomingTopics: IncomingTopicsReducer.reducer,
  fsIncomingTopicsV2: IncomingTopicsV2Reducer.reducer,
  fsMachines: MachineReducer.reducer,
  fsServiceVans: ServiceVanReducer.reducer,
  fsFeatureToggles: FeatureToggleReducer.reducer,
  fsLogMessages: LogMessageReducer.reducer,
  fsTMachines: SharedMachinesReducer.reducer,
  fsTscAdmin: TscAdminReducer.reducer,
  fsDefaultViewFilter: DefaultViewFilterReducer.reducer,
  fsMaintenances: MaintenanceReducer.reducer,
};
